/**
 * "Manually Added Accounts" — accounts a person typed in, which the
 * depletions feed hasn't confirmed yet.
 *
 * The name states the distinguishing fact: a HUMAN entered these, the
 * VIP feed didn't. "New Accounts" would be ambiguous (a feed account
 * can be new too) and anything possessive would be wrong — the tab is
 * territory-scoped, so a rep sees their colleagues' additions as well
 * as their own, which is why each card names who added it and when.
 *
 * WHY THIS TAB EXISTS
 * -------------------
 * Today's Plan and My Territories are both built on
 * `get_follow_up_tracker`, which INNER JOINs `depletions.facts` and
 * buckets by *days since last depletion*. An account with no facts —
 * which every hand-added account is, by definition — can never appear
 * in either. Without this tab a rep could add an account, log one
 * visit from the page they were redirected to, and then never find it
 * again except by URL.
 *
 * It's also the honest place for them: the tracker's buckets measure
 * depletion recency, so dropping a zero-fact account into "120+ days"
 * would claim it's gone stale when in fact it's brand new.
 *
 * The tab empties itself. Once a depletions file matches an account on
 * its natural key, the ingest promotes it to `source: 'vip'` and it
 * moves into the normal tracker with real volume behind it.
 *
 * WHY CARDS, NOT A TABLE
 * ----------------------
 * Each account can carry a possible duplicate needing a decision, and
 * a flat table has nowhere to put a nested block with its own actions
 * without wrecking the column grid. Volume and product columns are
 * deliberately absent either way — they come from the feed and
 * genuinely don't exist yet, and empty cells read as broken data.
 */

import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { Alert, Box, Button, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSectionExport } from '../../hooks/useSectionExport';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  premisesLabel,
  type ExcelWorkbookSpec,
} from '../../util/excel-export';

import {
  dismissDuplicate,
  fetchManualAccounts,
  VISIT_CHANNEL_LABELS,
  VISIT_OUTCOME_LABELS,
  type AccountMatch,
  type ManualAccountRow,
} from '../../api/field';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = '#fdf9f0';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

/** Cards read better at a bounded measure — at full width the actions
 *  drift a long way from the account they act on. */
const MAX_WIDTH = 940;

function addedWhen(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface Props {
  onAddAccount: () => void;
  onLogVisit: (account: { accountId: number; accountName: string }) => void;
}

export function AddedAccountsTab({ onAddAccount, onLogVisit }: Props) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['field', 'manual-accounts'],
    queryFn: fetchManualAccounts,
    staleTime: 30_000,
  });

  const dismiss = useMutation({
    mutationFn: ({ accountId, otherId }: { accountId: number; otherId: number }) =>
      dismissDuplicate(accountId, otherId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['field', 'manual-accounts'] });
    },
  });

  const items = query.data?.items ?? [];
  const needsReview = query.data?.needs_review ?? 0;

  // The endpoint returns the whole list (no paging), in the order the
  // cards show it. County isn't on the row, so that column is dropped
  // rather than exported empty.
  useSectionExport(
    (): ExcelWorkbookSpec | null => {
      const rows = items.map((r) => ({ ...r.account, suggestions: r.suggestions }));
      type Row = (typeof rows)[number];
      return {
        title: 'Manually Added Accounts',
        filters: [
          {
            kind: 'info',
            label: 'Showing',
            value: 'Hand-added accounts in your territory not yet confirmed by a depletions file',
          },
          { kind: 'info', label: 'Needing duplicate review', value: String(needsReview) },
        ],
        sheets: [
          excelSheet({
            name: 'Added Accounts',
            rows,
            columns: [
              ...accountColumns<Row>().filter((c) => c.header !== 'County'),
              {
                header: 'Premises',
                value: (r) => (r.premises_type ? premisesLabel(r.premises_type) : ''),
                width: 12,
              },
              { header: 'Added By', value: (r) => r.added_by_name, width: 20 },
              { header: 'Added On', value: (r) => isoDate(r.added_at), format: 'date' },
              {
                header: 'Status',
                value: (r) =>
                  r.suggestions.length > 0 ? 'Possible duplicate — needs review' : 'Awaiting feed',
                width: 30,
              },
              {
                header: 'Possible Duplicates',
                value: (r) =>
                  r.suggestions
                    .map((s) => {
                      const where = [s.city, s.state_code].filter(Boolean).join(', ');
                      return `${s.name}${where ? ` (${where})` : ''} — ${Math.round(s.similarity * 100)}%`;
                    })
                    .join('; '),
                width: 40,
              },
              { header: 'Last Visit', value: (r) => isoDate(r.last_visit_date), format: 'date' },
              {
                header: 'Days Since Visit',
                value: (r) => num(r.days_since_last_visit),
                format: 'int',
              },
              {
                header: 'Last Outcome',
                value: (r) => (r.last_outcome ? VISIT_OUTCOME_LABELS[r.last_outcome] : ''),
                width: 18,
              },
              {
                header: 'Last Channel',
                value: (r) => (r.last_channel ? VISIT_CHANNEL_LABELS[r.last_channel] : ''),
                width: 16,
              },
              { header: 'Last Note', value: (r) => r.last_note_excerpt, width: 50 },
            ],
          }),
        ],
      };
    },
    query.isSuccess && items.length > 0,
  );

  if (query.isPending) {
    return (
      <Stack gap={1.5} sx={{ maxWidth: MAX_WIDTH }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={104} />
        ))}
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error" sx={{ fontFamily: FONT_INTER, maxWidth: MAX_WIDTH }}>
        Couldn’t load your added accounts.
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          maxWidth: MAX_WIDTH,
          textAlign: 'center',
          py: 6,
          px: 2,
          border: '1px dashed rgba(187, 140, 63, 0.35)',
          borderRadius: 2,
          bgcolor: IVORY,
        }}
      >
        <AddBusinessOutlinedIcon sx={{ fontSize: 34, color: 'rgba(187, 140, 63, 0.6)', mb: 1 }} />
        <Typography
          sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 700, color: INK, mb: 0.5 }}
        >
          Nothing added yet
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.6)',
            maxWidth: 460,
            mx: 'auto',
            mb: 2,
          }}
        >
          Walked into a place that isn’t on your list? Add it here and log the visit — it moves into
          your territory automatically once it shows up in a depletions file.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddBusinessOutlinedIcon />}
          onClick={onAddAccount}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            color: GOLD,
            borderColor: 'rgba(187, 140, 63, 0.5)',
          }}
        >
          Add an Account
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDTH }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 13,
          color: 'rgba(15, 10, 7, 0.62)',
          mb: 2,
          lineHeight: 1.55,
        }}
      >
        Accounts entered by hand across your territory that no depletions file has confirmed yet.
        They carry no shipment volume, so they sit here rather than in your territory buckets — and
        move across on their own once the data catches up.
      </Typography>

      {/* Only when there's actually a decision waiting. A permanent
          banner reading "0 need review" is chrome. */}
      {needsReview > 0 && (
        <Alert
          severity="warning"
          icon={<MergeTypeIcon fontSize="inherit" />}
          sx={{ mb: 2, fontFamily: FONT_INTER, fontSize: 13, borderRadius: 2 }}
        >
          <strong>
            {needsReview} {needsReview === 1 ? 'account looks' : 'accounts look'}
          </strong>{' '}
          like {needsReview === 1 ? 'one' : 'ones'} that already exist. Open the existing account,
          or mark it as a different place.
        </Alert>
      )}

      <Stack gap={1.5}>
        {items.map((row) => (
          <ManualAccountCard
            key={row.account.account_id}
            row={row}
            onLogVisit={onLogVisit}
            onDismiss={(otherId) => dismiss.mutate({ accountId: row.account.account_id, otherId })}
            dismissing={dismiss.isPending}
          />
        ))}
      </Stack>
    </Box>
  );
}

function ManualAccountCard({
  row,
  onLogVisit,
  onDismiss,
  dismissing,
}: {
  row: ManualAccountRow;
  onLogVisit: (account: { accountId: number; accountName: string }) => void;
  onDismiss: (otherAccountId: number) => void;
  dismissing: boolean;
}) {
  const navigate = useNavigate();
  const a = row.account;
  const location = [a.address, a.city, a.state_code].filter(Boolean).join(', ');
  const added = addedWhen(a.added_at);
  const hasSuggestions = row.suggestions.length > 0;

  const open = () => navigate(`/regional-sales/accounts/${a.account_id}`);

  return (
    <Box
      sx={{
        border: '1px solid',
        // A card awaiting a decision reads as unresolved rather than
        // sitting flat among the settled ones.
        borderColor: hasSuggestions ? 'rgba(178, 47, 47, 0.32)' : 'rgba(15, 10, 7, 0.09)',
        borderRadius: 2,
        bgcolor: '#fff',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15, 10, 7, 0.03), 0 6px 18px rgba(15, 10, 7, 0.04)',
        transition: 'box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          borderColor: hasSuggestions ? 'rgba(178, 47, 47, 0.45)' : 'rgba(187, 140, 63, 0.42)',
          boxShadow: '0 2px 6px rgba(15, 10, 7, 0.05), 0 12px 28px rgba(15, 10, 7, 0.06)',
        },
      }}
    >
      <Box sx={{ px: 2.25, py: 1.9 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          gap={1.5}
        >
          <Box
            onClick={open}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open();
              }
            }}
            sx={{
              minWidth: 0,
              flexGrow: 1,
              cursor: 'pointer',
              '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 3 },
            }}
          >
            <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 15, fontWeight: 700, color: INK }}
              >
                {a.name}
              </Typography>
              {a.premises_type && (
                <Chip
                  size="small"
                  label={a.premises_type === 'ON' ? 'On-premise' : 'Off-premise'}
                  sx={{
                    height: 18,
                    fontFamily: FONT_INTER,
                    fontSize: 9.5,
                    fontWeight: 700,
                    bgcolor: 'rgba(50, 110, 184, 0.10)',
                    color: '#326eb8',
                  }}
                />
              )}
            </Stack>

            <Typography
              sx={{
                mt: 0.35,
                fontFamily: FONT_INTER,
                fontSize: 12.5,
                color: 'rgba(15, 10, 7, 0.55)',
              }}
            >
              {location || 'No address on file'}
            </Typography>

            {/* Provenance and status on one muted line — who put it
                here, when, and whether anyone has been since. */}
            <Typography
              sx={{
                mt: 0.75,
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.45)',
              }}
            >
              {a.added_by_name ? `Added by ${a.added_by_name}` : 'Manually added'}
              {added ? ` · ${added}` : ''}
              {' · '}
              {a.last_visit_date ? (
                <>
                  Last visit {a.last_visit_date}
                  {a.last_outcome ? ` · ${VISIT_OUTCOME_LABELS[a.last_outcome]}` : ''}
                </>
              ) : (
                'Not visited yet'
              )}
            </Typography>
          </Box>

          <Button
            size="small"
            startIcon={<EditNoteIcon sx={{ fontSize: 16 }} />}
            onClick={() => onLogVisit({ accountId: a.account_id, accountName: a.name })}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: 'flex-start', sm: 'center' },
              fontFamily: FONT_INTER,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12.5,
              color: GOLD,
              border: '1px solid rgba(187, 140, 63, 0.4)',
              borderRadius: 1.5,
              px: 1.5,
              '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
            }}
          >
            Log visit
          </Button>
        </Stack>
      </Box>

      {hasSuggestions && (
        <Box
          sx={{
            px: 2.25,
            py: 1.6,
            bgcolor: 'rgba(178, 47, 47, 0.035)',
            borderTop: '1px solid rgba(178, 47, 47, 0.16)',
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#b22f2f',
              mb: 1,
            }}
          >
            {row.suggestions.length === 1
              ? 'This may already exist'
              : `${row.suggestions.length} accounts look like this one`}
          </Typography>

          <Stack gap={0.75}>
            {row.suggestions.map((s) => (
              <SuggestionRow
                key={s.account_id}
                suggestion={s}
                onOpen={() => navigate(`/regional-sales/accounts/${s.account_id}`)}
                onDismiss={() => onDismiss(s.account_id)}
                dismissing={dismissing}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function SuggestionRow({
  suggestion,
  onOpen,
  onDismiss,
  dismissing,
}: {
  suggestion: AccountMatch;
  onOpen: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  const location =
    [suggestion.address, suggestion.city, suggestion.state_code].filter(Boolean).join(', ') ||
    'No address on file';

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      gap={1}
      sx={{
        px: 1.5,
        py: 1,
        bgcolor: '#fff',
        border: '1px solid rgba(15, 10, 7, 0.08)',
        borderRadius: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" alignItems="baseline" gap={0.75}>
          <Typography
            noWrap
            sx={{ fontFamily: FONT_INTER, fontSize: 13, fontWeight: 700, color: INK }}
          >
            {suggestion.name}
          </Typography>
          {/* How close the names are. Surfaced so a rep can tell
              "almost certainly the same place" from "worth a glance"
              rather than treating every suggestion as equally likely. */}
          <Tooltip title="How closely the names match">
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15,10,7,0.4)' }}>
              {Math.round(suggestion.similarity * 100)}% match
            </Typography>
          </Tooltip>
        </Stack>
        <Typography
          noWrap
          sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15,10,7,0.5)' }}
        >
          {location}
        </Typography>
      </Box>

      <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          endIcon={<NorthEastIcon sx={{ fontSize: 13 }} />}
          onClick={onOpen}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            color: GOLD,
          }}
        >
          Open
        </Button>
        <Button
          size="small"
          onClick={onDismiss}
          disabled={dismissing}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          Not a duplicate
        </Button>
      </Stack>
    </Stack>
  );
}
